import { environment } from '../../config/environment';


// Middleware SDK Type Definitions
interface MiddlewareTrackConfig {
    serviceName: string;
    projectName: string;
    accountKey: string;
    target: string;
    sessionRecording: boolean;
    tracePropagationTargets: RegExp[];
    defaultAttributes: Record<string, string>;
}

interface MiddlewareSDK {
    track(config: MiddlewareTrackConfig): void;
    sendCustomEvent(name: string, context?: object): void;
    addTiming(name: string): void;
    sendError(error: Error, context?: object): void;
    setAttributes(attributes: Record<string, string>): void;
    error(message: string, context?: object): void;
    info(message: string, context?: object): void;
    warn(message: string, context?: object): void;
}

declare global {
    interface Window {
        Middleware?: MiddlewareSDK;
    }
}

class MiddlewareService {
    private initialized = false;
    private middleware: MiddlewareSDK | null = null;

    initialize(): void {
        if (!environment.middleware.enabled || this.initialized) return;

        // Check if Middleware SDK is loaded via CDN script in index.html
        if (typeof window.Middleware === 'undefined') {
            console.warn('Middleware SDK not loaded. Make sure the CDN script is added to index.html');
            return;
        }

        // Initialize Middleware RUM
        this.middleware = window.Middleware;
        this.middleware.track({
            serviceName: environment.middleware.serviceName,
            projectName: environment.middleware.projectName,
            accountKey: environment.middleware.accountKey,
            target: environment.middleware.target,
            sessionRecording: environment.middleware.sessionRecording,
            tracePropagationTargets: [/.*/],
            defaultAttributes: {
                'app.environment': environment.middleware.env,
                'app.version': environment.middleware.version,
            },
        });

        this.initialized = true;
    }

    // Track custom actions
    trackAction(name: string, context?: object): void {
        if (this.initialized && this.middleware) {
            this.middleware.sendCustomEvent(name, context);
        }
    }

    // Add custom timing measurements
    addTiming(name: string): void {
        if (this.initialized && this.middleware) {
            this.middleware.addTiming(name);
        }
    }

    // Add custom error tracking
    addError(error: Error, context?: object): void {
        if (this.initialized && this.middleware) {
            this.middleware.sendError(error, context);
        }
    }

    // Set user context for RUM using setAttributes
    // Middleware.io SDK uses setAttributes() to update user context after initialization
    // Captures non-PII attributes only: device id, org/site identifiers, visitor type,
    // and the opaque preregistration id. Visitor name and email are intentionally
    // excluded pending privacy/legal sign-off.
    setUser(user: {
        id?: string;
        orgId?: string;
        orgName?: string;
        siteId?: string;
        siteName?: string;
        visitorType?: string;
        preregistrationId?: string;
        [key: string]: unknown;
    }): void {
        if (!this.initialized || !this.middleware || !user) return;

        try {
            const attributes: { [key: string]: string } = {};

            // Legacy keys (kept for back-compat with existing dashboards/alerts)
            if (user.id) attributes['user.id'] = String(user.id);
            if (user.orgId != null) attributes['user.orgId'] = String(user.orgId);

            // Org / site context
            if (user.orgId != null) attributes['org.id'] = String(user.orgId);
            if (user.orgName) attributes['org.name'] = String(user.orgName);
            if (user.siteId != null) attributes['site.id'] = String(user.siteId);
            if (user.siteName) attributes['site.name'] = String(user.siteName);

            // Visitor context (non-PII only)
            if (user.visitorType) attributes['visitor.type'] = String(user.visitorType);
            if (user.preregistrationId != null) {
                attributes['visitor.preregistrationId'] = String(user.preregistrationId);
            }

            // Set all attributes at once
            if (Object.keys(attributes).length > 0) {
                this.middleware.setAttributes(attributes);
            }
        } catch (error) {
            console.warn('Middleware: Failed to set user attributes', error);
        }
    }

    // Set additional user properties
    setUserProperty(key: string, value: unknown): void {
        if (!this.initialized || !this.middleware) return;

        try {
            this.middleware.setAttributes({ [key]: String(value) });
        } catch (error) {
            console.warn('Middleware: Failed to set user property', error);
        }
    }

    // Clear user context (useful for logout)
    clearUser(): void {
        if (!this.initialized || !this.middleware) return;

        try {
            this.middleware.setAttributes({
                'user.id': '',
                'user.orgId': '',
                'org.id': '',
                'org.name': '',
                'site.id': '',
                'site.name': '',
                'visitor.type': '',
                'visitor.preregistrationId': ''
            });
        } catch (error) {
            console.warn('Middleware: Failed to clear user attributes', error);
        }
    }

    // Log error
    logError(message: string, context?: object, error?: Error): void {
        if (this.initialized && this.middleware) {
            this.middleware.error(JSON.stringify({ message, context, error }));
        }
    }

    // Log info
    logInfo(message: string, context?: object): void {
        if (this.initialized && this.middleware) {
            this.middleware.info(JSON.stringify({message, context}));
        }
    }

    // Log warning
    logWarn(message: string, context?: object): void {
        if (this.initialized && this.middleware) {
            this.middleware.warn(JSON.stringify({message, context}));
        }
    }

    /**
     * Enable session recording on demand.
     * Call this method when you need to start capturing session replay.
     * Note: This reinitializes the SDK with session recording enabled.
     */
    enableSessionRecording(): void {
        if (!this.initialized || !this.middleware) {
            console.warn('Middleware not initialized. Cannot enable session recording.');
            return;
        }

        this.middleware.track({
            serviceName: environment.middleware.serviceName,
            projectName: environment.middleware.projectName,
            accountKey: environment.middleware.accountKey,
            target: environment.middleware.target,
            sessionRecording: true,
            tracePropagationTargets: [/.*/],
            defaultAttributes: {
                'app.environment': environment.middleware.env,
                'app.version': environment.middleware.version,
            },
        });

        console.info('Middleware session recording enabled');
    }

    /**
     * Disable session recording.
     * Call this method when you want to stop capturing session replay.
     */
    disableSessionRecording(): void {
        if (!this.initialized || !this.middleware) {
            console.warn('Middleware not initialized. Cannot disable session recording.');
            return;
        }

        this.middleware.track({
            serviceName: environment.middleware.serviceName,
            projectName: environment.middleware.projectName,
            accountKey: environment.middleware.accountKey,
            target: environment.middleware.target,
            sessionRecording: false,
            tracePropagationTargets: [/.*/],
            defaultAttributes: {
                'app.environment': environment.middleware.env,
                'app.version': environment.middleware.version,
            },
        });

        console.info('Middleware session recording disabled');
    }
}

export const middlewareService = new MiddlewareService();
